import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Loader2, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import EloVoteButtons, { VOTE_PRESETS, EloScoreDisplay } from './EloVoteButtons';
import { VOTE_ICON_THEMES } from './NestedComments';
import { useClubSettings } from '../context/ClubSettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin component to configure ELO vote display settings
 */
const VoteEmojiSettings = ({ clubId = 'default', onSave }) => {
  const { updateVoteSettings } = useClubSettings();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    vote_emoji_up: '🐾',
    vote_emoji_down: '❌',
    vote_theme: 'dogs',
    show_elo_temperature: true,
  });
  const [selectedPreset, setSelectedPreset] = useState('paws');
  const [useCustom, setUseCustom] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [clubId]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/clubs/${clubId}`, {
        credentials: 'include',
      });
      
      if (res.ok) {
        const club = await res.json();
        setSettings({
          vote_emoji_up: club.vote_emoji_up || '🐾',
          vote_emoji_down: club.vote_emoji_down || '❌',
          vote_theme: club.vote_theme || 'dogs',
          show_elo_temperature: club.show_elo_temperature ?? true,
        });
        
        // Detect which preset matches
        const matchingPreset = Object.entries(VOTE_PRESETS).find(
          ([, preset]) => preset.up === club.vote_emoji_up && preset.down === club.vote_emoji_down
        );
        if (matchingPreset) {
          setSelectedPreset(matchingPreset[0]);
          setUseCustom(false);
        } else {
          setUseCustom(true);
        }
      }
    } catch (err) {
      console.error('Failed to fetch club settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePresetChange = (preset) => {
    setSelectedPreset(preset);
    setUseCustom(false);
    setSettings({
      ...settings,
      vote_emoji_up: VOTE_PRESETS[preset].up,
      vote_emoji_down: VOTE_PRESETS[preset].down,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/clubs/${clubId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        toast.success('Vote display settings saved!');
        // Update the global club settings context so changes reflect immediately
        updateVoteSettings(settings);
        if (onSave) onSave(settings);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({
      vote_emoji_up: '🐾',
      vote_emoji_down: '❌',
      vote_theme: 'dogs',
      show_elo_temperature: true,
    });
    setSelectedPreset('paws');
    setUseCustom(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <Card data-testid="vote-emoji-settings">
      <CardHeader>
        <CardTitle data-testid="vote-settings-title">Vote Display Settings</CardTitle>
        <CardDescription>
          Customize how votes are displayed throughout the club
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Preset Selection */}
        <div className="space-y-3">
          <Label className="text-base font-medium">Emoji Style</Label>
          <RadioGroup
            value={useCustom ? 'custom' : selectedPreset}
            onValueChange={(val) => {
              if (val === 'custom') {
                setUseCustom(true);
              } else {
                handlePresetChange(val);
              }
            }}
            className="grid grid-cols-2 gap-3"
          >
            {Object.entries(VOTE_PRESETS).map(([key, preset]) => (
              <div key={key} className="flex items-center space-x-2">
                <RadioGroupItem value={key} id={`preset-${key}`} />
                <Label 
                  htmlFor={`preset-${key}`} 
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <span className="text-lg">{preset.up}</span>
                  <span className="text-lg">{preset.down}</span>
                  <span className="text-sm text-muted-foreground">({preset.name})</span>
                </Label>
              </div>
            ))}
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="custom" id="preset-custom" />
              <Label htmlFor="preset-custom" className="cursor-pointer">
                Custom Emojis
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* Custom Emoji Input */}
        {useCustom && (
          <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
            <div className="space-y-2">
              <Label htmlFor="emoji-up">Upvote Emoji</Label>
              <Input
                id="emoji-up"
                value={settings.vote_emoji_up}
                onChange={(e) => setSettings({ ...settings, vote_emoji_up: e.target.value })}
                placeholder="🐾"
                className="text-center text-2xl h-12"
                maxLength={4}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emoji-down">Downvote Emoji</Label>
              <Input
                id="emoji-down"
                value={settings.vote_emoji_down}
                onChange={(e) => setSettings({ ...settings, vote_emoji_down: e.target.value })}
                placeholder="❌"
                className="text-center text-2xl h-12"
                maxLength={4}
              />
            </div>
            <p className="col-span-2 text-xs text-muted-foreground">
              Tip: You can use any emoji! Try searching "emoji picker" or use keyboard shortcuts.
            </p>
          </div>
        )}

        {/* Temperature Toggle */}
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div>
            <Label className="text-base font-medium">Show Temperature Indicator</Label>
            <p className="text-sm text-muted-foreground">
              Display warm/cold visual based on ELO score
            </p>
          </div>
          <Switch
            checked={settings.show_elo_temperature}
            onCheckedChange={(checked) => setSettings({ ...settings, show_elo_temperature: checked })}
          />
        </div>

        {/* Comment Vote Theme */}
        <div className="space-y-3">
          <Label className="text-base font-medium">Comment Vote Icons</Label>
          <p className="text-sm text-muted-foreground mb-2">
            Choose the icon theme for upvote/downvote buttons on forum comments
          </p>
          <RadioGroup
            value={settings.vote_theme}
            onValueChange={(val) => setSettings({ ...settings, vote_theme: val })}
            className="grid grid-cols-2 sm:grid-cols-3 gap-3"
          >
            {Object.entries(VOTE_ICON_THEMES).map(([key, theme]) => (
              <div key={key} className="flex items-center space-x-2">
                <RadioGroupItem value={key} id={`theme-${key}`} />
                <Label 
                  htmlFor={`theme-${key}`} 
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <span className="text-lg">{theme.up}</span>
                  <span className="text-lg">{theme.down}</span>
                  <span className="text-xs text-muted-foreground">({theme.name})</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
          <div className="mt-2 p-3 bg-muted/30 rounded-lg">
            <p className="text-xs text-muted-foreground mb-2">Preview:</p>
            <div className="flex items-center gap-2">
              <span className="text-xl opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all cursor-pointer">
                {VOTE_ICON_THEMES[settings.vote_theme]?.up || '🐕'}
              </span>
              <span className="text-xl opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all cursor-pointer">
                {VOTE_ICON_THEMES[settings.vote_theme]?.down || '🐺'}
              </span>
              <span className="text-xs text-muted-foreground ml-2">
                (hover to see active state)
              </span>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="space-y-3">
          <Label className="text-base font-medium">Preview</Label>
          <div className="p-4 bg-muted/30 rounded-lg border">
            <div className="flex flex-wrap items-center gap-6 justify-center">
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-2">Hot (+32)</p>
                <EloVoteButtons
                  upvotes={42}
                  downvotes={10}
                  userVote={1}
                  emojiUp={settings.vote_emoji_up}
                  emojiDown={settings.vote_emoji_down}
                  showTemperature={settings.show_elo_temperature}
                  eloScore={1850}
                  disabled
                />
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-2">Neutral (+5)</p>
                <EloVoteButtons
                  upvotes={15}
                  downvotes={10}
                  userVote={0}
                  emojiUp={settings.vote_emoji_up}
                  emojiDown={settings.vote_emoji_down}
                  showTemperature={settings.show_elo_temperature}
                  eloScore={1250}
                  disabled
                />
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground mb-2">Cold (-15)</p>
                <EloVoteButtons
                  upvotes={5}
                  downvotes={20}
                  userVote={-1}
                  emojiUp={settings.vote_emoji_up}
                  emojiDown={settings.vote_emoji_down}
                  showTemperature={settings.show_elo_temperature}
                  eloScore={850}
                  disabled
                />
              </div>
            </div>
          </div>
        </div>

        {/* Temperature Legend */}
        {settings.show_elo_temperature && (
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Temperature Scale</Label>
            <div className="flex flex-wrap gap-2">
              <EloScoreDisplay score={1850} showTemperature size="sm" />
              <EloScoreDisplay score={1550} showTemperature size="sm" />
              <EloScoreDisplay score={1250} showTemperature size="sm" />
              <EloScoreDisplay score={950} showTemperature size="sm" />
              <EloScoreDisplay score={750} showTemperature size="sm" />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-4">
          <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="vote-settings-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Settings
          </Button>
          <Button variant="outline" onClick={handleReset} className="gap-2" data-testid="vote-settings-reset-btn">
            <RotateCcw className="w-4 h-4" />
            Reset to Default
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default VoteEmojiSettings;
